/**
 * Certificate Monitor Service (frontend)
 * Anropar SysAdmin-API:t för certifikatbevakning.
 */

import api from '../api/client';

export type CertStatus = 'ok' | 'expiring' | 'expired' | 'unknown';

export interface CertificateItem {
  id: number;
  name: string;
  cert_type: string;
  source: 'auto' | 'manual';
  source_ref: string | null;
  organization_id: string | null;
  organization_name: string | null;
  subject: string | null;
  issuer: string | null;
  serial_number: string | null;
  thumbprint_sha1: string | null;
  valid_from: string | null;
  valid_to: string | null;
  check_host: string | null;
  warning_days: number;
  renewal_instructions: string | null;
  contact_email: string | null;
  notes: string | null;
  is_active: number;
  last_checked_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  days_left: number | null;
  status: CertStatus;
}

export interface CertificateSummary {
  expiring: number;
  expired: number;
  items: CertificateItem[];
}

export interface ManualCertInput {
  name: string;
  certificate_pem?: string | null;
  organization_id?: string | null;
  organization_name?: string | null;
  valid_from?: string | null;
  valid_to?: string | null;
  check_host?: string | null;
  warning_days?: number | null;
  renewal_instructions?: string | null;
  contact_email?: string | null;
  notes?: string | null;
  is_active?: boolean;
}

const BASE = '/api/sysadmin/certificates';

export const certificateMonitorService = {
  async list(): Promise<CertificateItem[]> {
    const res = await api.get(BASE);
    return res.data?.data ?? [];
  },

  async getSummary(): Promise<CertificateSummary> {
    const res = await api.get(`${BASE}/summary`);
    return res.data?.data ?? { expiring: 0, expired: 0, items: [] };
  },

  async refresh(): Promise<{
    scanned: number;
    updated: number;
    errors: number;
    oidcScanned?: number;
    oidcUpdated?: number;
    hostScanned?: number;
    hostUpdated?: number;
  }> {
    const res = await api.post(`${BASE}/refresh`);
    return res.data?.data;
  },

  async create(input: ManualCertInput): Promise<CertificateItem> {
    const res = await api.post(BASE, input);
    return res.data?.data;
  },

  async update(id: number, input: Partial<ManualCertInput>): Promise<CertificateItem> {
    const res = await api.put(`${BASE}/${id}`, input);
    return res.data?.data;
  },

  async remove(id: number): Promise<void> {
    await api.delete(`${BASE}/${id}`);
  },
};

export default certificateMonitorService;
